import t from "../../../../../../i18n";
import { FILE_PARSING_ERROR } from "../../../../../../domain/parser/common/file";
import type { ParsingError, ParsingErrorMessageMap } from "../../../../../../domain/parser/types";

export type FileKeys = "main" | "variables" | "parameters" | "binding" | "image" | "nonExpected";

export enum FileType {
  dymola = "DYMOLA",
  fmuTo = "FMU_T0",
  fmuTf = "FMU_TF",
}

export enum ErrorMessage {
  missingColumn = "Missing column",
  unknownQuantity = "Unknown quantity",
  offsetExpectedNotANumber = "OffsetExpected is not a number",
  invalidExternalGap = "Invalid external gap",
  invalidOrderExternalGap = "Invalid order external gap",
  invalidReferenceValue = "Invalid reference value",
  invalidOrderExternalAscendingArrayStartingWithOne = "Invalid order external ascending array starting with one",
  CannotDependOnPastFunction = "Cannot depend on past_value or time_since function",
}

export const expectedFormats = ["svg", "png", "csv"];
export const expectedBindingFormat = [
  "name",
  "inputModelName",
  "offsetExpected",
  "outputModelName",
  "orderExternalGap",
  "referenceValue",
] as const;
export type BindingColumn = (typeof expectedBindingFormat)[number];

export const CSVColumnMapping = new Map<BindingColumn, string[]>();
CSVColumnMapping.set("name", ["name", "technicalName"]);
CSVColumnMapping.set("inputModelName", ["inputModelName", "boundaryConditionInputModelName"]);
CSVColumnMapping.set("offsetExpected", ["offsetExpected"]);
CSVColumnMapping.set("outputModelName", ["outputModelName"]);
CSVColumnMapping.set("orderExternalGap", ["orderExternalGap"]);
CSVColumnMapping.set("referenceValue", ["referenceValue"]);

export const CSVColumnMappingMandatory = new Map<BindingColumn, string[]>();
CSVColumnMappingMandatory.set("name", ["name", "technicalName"]);
CSVColumnMappingMandatory.set("inputModelName", [
  "inputModelName",
  "boundaryConditionInputModelName",
]);
CSVColumnMappingMandatory.set("outputModelName", ["outputModelName"]);

export interface MapFile {
  fileName: string[];
  error?: { message: string; data: string[] }[];
}
export type ErrorType = "info" | "error";

const types = ["DYMOLA", "FMU_T0", "FMU_TF"] as const;
export type TypeModel = (typeof types)[number];

export function isModelType(value: string): value is TypeModel {
  if (FileType.dymola === value || FileType.fmuTf === value || FileType.fmuTo === value)
    return true;
  return false;
}

/** -------------------------------------------------------------------------------------
 *                                     Image Model
 * -------------------------------------------------------------------------------------
 */

/**
 * @description Error messages that can be presented to the user by the image model.
 */
export const IMAGE_MODEL_PARSING_ERROR_MESSAGES: ParsingErrorMessageMap<FILE_PARSING_ERROR> = {
  [FILE_PARSING_ERROR.FILE_INVALID_TYPE]: () =>
    t("setup.simulation.image_model.import.error.wrong_file_format"),
  [FILE_PARSING_ERROR.FILE_SIZE_LIMIT_EXCEEDED]: () =>
    t("setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded"),
  [FILE_PARSING_ERROR.FILE_EMPTY]: () =>
    t("setup.diagnosis.observable.import_observables.error.empty_file"),
};
/**
 * @description Get translated error message.
 */
export const getImageModelParsingErrorMessage = (err: ParsingError<FILE_PARSING_ERROR>) =>
  IMAGE_MODEL_PARSING_ERROR_MESSAGES[err.kind]?.(err.data) ?? "Unknown error";
